/**
 * @file index.tsx
 * @author markJia(markjia@tencent.com)
 */

import _ from 'lodash';
import React, { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useMemoizedFn, useSetState } from 'ahooks';
import Icon from '@tencent/adq-icons';
import { message } from '@tencent/spaui';
import { Loading } from '@tencent/ptp-fe-common';
import ReactQrCode from 'react-qr-code';
import copyToClipboard from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { SINGLE_BASENAME } from '@/app/single';
import { computeGetUrl } from '@/utils';
import AI_AGENT_REPORT_EVENTS from '../../constants/report';
import { uploadFile, addBoard, getAIDashboardList } from '../../../../api/analysis';
import aiDashboardStore from '../../../../stores/dashboard';

// 页面分析内网页面，需cm免登陆走通流程
const ANALYZE_PAGE_BASE = 'https://adp-fe-docs.woa.com/ai-chat-react/iframe.html?globals=&id=%E9%A1%B5%E9%9D%A2-analyzertoolkit--page-analyzer-toolkit-story&viewMode=story';

interface QrPreviewProps {
  codeText: string;
  userInfo?: Record<string, any>;
  chatSessionId: string;
  enableReportAnalyze?: boolean;
}

export const QrPreview = (props: QrPreviewProps) => {
  const history = useHistory();
  const { codeText, userInfo, chatSessionId, enableReportAnalyze } = props;

  const [state, updateState] = useSetState<{
    saveLoading: boolean;
    analyzeLoading: boolean;
    shareLoading: boolean;
    previewLoading: 'previewLoading' | 'error' | 'finish';
    previewUrl: string;
    downloadUrl: string;
  }>({
    analyzeLoading: false,
    saveLoading: false,
    shareLoading: false,
    previewLoading: 'previewLoading',
    previewUrl: '',
    downloadUrl: '',
  });

  const getBoardUrl = useMemoizedFn(async (): Promise<{ url: string; fileName: string; }> => {
    const file = new File([codeText], `preview_${Date.now()}.html`, {
      type: 'text/html',
      lastModified: Date.now(),
    });

    const res = await uploadFile({ file });
    const { code, data, message: errMsg = '服务异常，请稍后重试' } = res || {};
    if (code !== 0) {
      throw new Error(errMsg);
    }

    const url = _.get(data, 'file_url', '');
    const fileName = _.get(data, 'file_name', '');

    if (!url) {
      throw new Error('url is empty');
    }

    return {
      url,
      fileName,
    };
  });

  const handleAIDashboardListRefresh = async () => {
    const latestAIDashboardList = await getAIDashboardList();
    aiDashboardStore.setAIDashboardList(latestAIDashboardList);
    // 重定向到其他看板
    if (latestAIDashboardList?.length > 0) {
      history.replace(`/analysis/ai-dashboard/${_.takeRight(latestAIDashboardList)[0].board_id}`);
    }
  };

  const saveToList = async () => {
    if (state.saveLoading) {
      return;
    }

    try {
      updateState({ saveLoading: true });
      const { url: downloadUrl } = await getBoardUrl();
      const boardName = codeText.match(/<title>(.+)<\/title>*?/)?.[1] || dayjs(Date.now()).format('YYYY-MM-DD HH:mm');

      const saveRes: any = await addBoard({
        user_id: _.get(userInfo, 'user_id'),
        session_id: chatSessionId,
        board_name: boardName,
        board_url: downloadUrl,
      });

      if (saveRes?.code !== 0) {
        message.error('保存失败，请检查');
        return;
      }

      message.success('保存看板成功');
      handleAIDashboardListRefresh();
    } finally {
      updateState({ saveLoading: false });
    }
  };

  const onShare = useMemoizedFn(async () => {
    if (state.shareLoading) {
      return;
    }

    try {
      updateState({ shareLoading: true });
      const { fileName } = await getBoardUrl();
      if (!fileName) {
        message.error('分享链接生成失败');
        return;
      }

      const previewUrl = computeGetUrl(`https://${window.location.hostname}${SINGLE_BASENAME}/ai-report-view?file_name=${fileName}&session_id=${chatSessionId}`);
      copyToClipboard(previewUrl);
      message.success('分享链接已复制至剪贴板');
    } catch (err) {
      message.error('分享链接生成失败');
    } finally {
      updateState({
        shareLoading: false,
      });
    }
  });

  const onPreview = useMemoizedFn(async () => {
    updateState({
      previewLoading: 'previewLoading',
    });

    try {
      const { url: downloadUrl, fileName } = await getBoardUrl();
      if (!downloadUrl || !fileName) {
        updateState({
          previewLoading: 'error',
        });
      }

      const previewUrl = `https://${window.location.hostname}${SINGLE_BASENAME}/ai-report-view?file_name=${fileName}&session_id=${chatSessionId}`;
      // const previewUrl = `https://testhub.e.qq.com/cm/report_view?_tde_id=48851&file_name=${filePath}`;
      updateState({
        previewLoading: 'finish',
        previewUrl,
        downloadUrl,
      });
    } catch (err) {
      updateState({
        previewLoading: 'error',
      });
      return;
    }
  });

  const reportAnalyze = useMemoizedFn(async () => {
    updateState({ analyzeLoading: true });
    try {
      const { fileName } = await getBoardUrl();
      const cookieStr = window.btoa(document.cookie);
      const analyzePage = `${ANALYZE_PAGE_BASE}&report_file_name=${fileName}&report_token=${cookieStr}`;
      window.open(analyzePage, '_blank');
    } finally {
      updateState({ analyzeLoading: false });
    }
  });

  const getQrCode = (): ReactNode => {
    if (state.previewLoading === 'previewLoading') {
      return <Loading className="tw-m-[100px] tw-h-[100px]" notFullScreen />;
    }

    if (state.previewLoading === 'error') {
      return <div>服务异常，请稍后重试</div>;
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-[12px] tw-items-center tw-p-[12px]">
        <div className="tw-flex tw-items-center tw-text-[18px] tw-font-[600] tw-whitespace-nowrap">
          你可以<div className="tw-text-blue-06">保存二维码</div>分享给成员
        </div>
        <ReactQrCode size={200} className="tw-flex-none" value={state.previewUrl} />
        <i className="tw-my-[12px] tw-bg-gray-06 tw-w-full tw-h-[1px]" />
        <div className="tw-flex tw-justify-end tw-w-full">
          <Icon
            className="tw-cursor-pointer hover:tw-text-blue-06"
            name="link"
            onClick={() => {
              copyToClipboard(state.previewUrl);
              message.success('复制成功');
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="tw-flex tw-gap-[8px]">
      <span
        className="tw-flex tw-items-center hover:tw-text-blue-06 tw-cursor-pointer"
        onClick={onShare}
        data-report={AI_AGENT_REPORT_EVENTS.AI_AGENT_SHARE_BOARD}
      >
        <Icon name="linkSquare" />&nbsp;{state.shareLoading ? '正在生成分享链接...' : '分享看板'}
      </span>
      {/* <OverlayTrigger trigger="click" overlay={<PopOver>{getQrCode()}</PopOver>} onShow={onPreview}>
        <span className="tw-flex tw-items-center hover:tw-text-blue-06 tw-cursor-pointer">
          <Icon name="linkSquare" />&nbsp;分享看板
        </span>
      </OverlayTrigger> */}
      <span
        className={classNames('tw-flex tw-items-center', state.saveLoading ? 'tw-text-[#ccc]' : 'hover:tw-text-blue-06 tw-cursor-pointer')}
        onClick={() => {
          saveToList();
        }}
        data-report={AI_AGENT_REPORT_EVENTS.AI_AGENT_SAVE_BOARD}
      >
        <Icon name="save" />&nbsp;{state.saveLoading ? '正在保存...' : '保存至看板'}
      </span>
      {enableReportAnalyze && (
        <span className='tw-flex tw-items-center hover:tw-text-blue-06 tw-cursor-pointer' onClick={() => {
          reportAnalyze();
        }}>
          <Icon name='verify' />&nbsp;看板评测
        </span>
      )}
    </div>
  );
};
